import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Loader from "./ui/Loader";
import "../App.css";
import { swalError, swalSuccess } from "../utils/swalAlert";

const serverUrl = process.env.REACT_APP_SERVER_URL;

const api = {
  post: async (endpoint, data) => {
    const response = await fetch(`${serverUrl}/api${endpoint}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw await response.json();
    return { data: await response.json(), status: response.status };
  },
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  // Get email/phoneNumber and token from URL params
  const params = new URLSearchParams(location.search);
  const email = params.get("email");
  const phoneNumber = params.get("phoneNumber");
  const token = params.get("token");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (formData.newPassword !== formData.confirmPassword) {
      swalError("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      const payload = {
        token,
        newPassword: formData.newPassword,
      };

      // Include either email or phoneNumber
      if (email) {
        payload.email = email;
      } else if (phoneNumber) {
        payload.phoneNumber = phoneNumber;
      }

      const response = await api.post("/user/reset-password", payload);

      if (response.status === 200) {
        swalSuccess("Password reset successful!");
        navigate("/login");
      }
    } catch (error) {
      const errorMessage = error.message || "Failed to reset password";
      swalError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.id]: e.target.value,
    }));
  };

  if ((!email && !phoneNumber) || !token) {
    return (
      <motion.div
        className="login-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <img src="/imgs/logo.svg" alt="GradeMe-AI Logo" width={400} />
        <motion.div
          className="login-box"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.h2
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.2 }}
            style={{ color: "#e74c3c", marginBottom: "1rem" }}
          >
            Invalid Reset Password Link
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            style={{ textAlign: "center", lineHeight: "1.5" }}
          >
            Please use the link sent to your {email ? "email" : "WhatsApp"} to reset your password.
            <br />
            <motion.button
              className="login-button"
              onClick={() => navigate("/login")}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ marginTop: "1rem" }}
            >
              Back to Login
            </motion.button>
          </motion.p>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="login-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <img src="/imgs/logo.svg" alt="GradeMe-AI Logo" width={400} />
      <motion.div
        className="login-box"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.h2
          initial={{ x: -50 }}
          animate={{ x: 0 }}
          transition={{ delay: 0.3 }}
        >
          Reset Password
        </motion.h2>
        <hr className="login__hr" />
        <motion.h3
          initial={{ y: -50 }}
          animate={{ y: 0 }}
          transition={{ delay: 0.3 }}
        >
          Enter New Password
        </motion.h3>
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <input
              type={showPassword ? "text" : "password"}
              id="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Enter new password"
              required
              minLength={6}
            />
            <label htmlFor="newPassword">
              <i className="fa-solid fa-asterisk requiredIcon"></i> New Password
            </label>
            <motion.span
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              <i
                className={`fa-regular ${
                  showPassword ? "fa-eye-slash" : "fa-eye"
                }`}
              />
            </motion.span>
          </div>
          <div className="input-group">
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm new password"
              required
              minLength={6}
            />
            <label htmlFor="confirmPassword">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Confirm
              Password
            </label>
            <motion.span
              className="toggle-password"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              <i
                className={`fa-regular ${
                  showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                }`}
              />
            </motion.span>
          </div>
          <motion.div
            className="login-actions"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            {!loading ? (
              <motion.button
                type="submit"
                className="login-button"
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
                }}
                whileTap={{ scale: 0.98 }}
              >
                Reset Password
              </motion.button>
            ) : (
              <Loader />
            )}
          </motion.div>
        </form>
      </motion.div>
    </motion.div>
  );
}